import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { AbstractLayerProjectComponent } from '@connect-services/client/components/abstract-layer-project/abstract-layer-project.component';
import { CreateFieldInterventionFooterComponent } from '@connect-services/client/components/create-field-intervention-footer/create-field-intervention-footer.component';
import { CreateFieldInterventionInformationComponent } from '@connect-services/client/components/create-field-intervention-information/create-field-intervention-information.component';
import { FieldInterventionEntitySelectionTableComponent } from '@connect-services/client/components/field-intervention-entity-selection-table/field-intervention-entity-selection-table.component';
import { LayerSelectComponent } from '@connect-services/client/components/layer-select/layer-select.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { FieldInterventionChangeSelectionService } from '@connect-services/client/services/field-intervention-change-selection.service';
import { FieldInterventionMapModeService } from '@connect-services/client/services/field-intervention-map-mode.service';
import { FieldInterventionSelectionModalService } from '@connect-services/client/services/field-intervention-selection-modal.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { MapMaskService } from '@connect-services/client/services/map/mask/map-mask.service';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { Layer } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CreateFieldInterventionFooterComponent,
        CreateFieldInterventionInformationComponent,
        FieldInterventionEntitySelectionTableComponent,
        LayerSelectComponent,
        LoadingComponent,
        RessourcesModule,
    ],
    selector: 'app-create-field-intervention',
    styleUrl: './create-field-intervention.component.scss',
    templateUrl: './create-field-intervention.component.html',
})
export class CreateFieldInterventionComponent extends AbstractLayerProjectComponent implements OnInit, OnDestroy {
    protected readonly createFieldInterventionService = inject(CreateFieldInterventionService);

    private readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    private readonly fieldInterventionChangeSelectionService = inject(FieldInterventionChangeSelectionService);
    private readonly fieldInterventionMapModeService = inject(FieldInterventionMapModeService);
    private readonly fieldInterventionSelectionModalService = inject(FieldInterventionSelectionModalService);
    private readonly layerListBloc = inject(LayerListBloc);
    private readonly layerService = inject(LayerService);
    private readonly mapMaskService = inject(MapMaskService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly selectionService = inject(SelectionService);

    public constructor() {
        super();
    }

    public async ngOnDestroy(): Promise<void> {
        this.fieldInterventionMapModeService.leaveFieldInterventionMapMode();
        this.mapMaskService.clearMasks();

        this.fieldInterventionSelectionModalService.closeSelectionModal();
        await this.selectionService.clearAllSelections();

        this.fieldInterventionChangeSelectionService.reset();
        this.createFieldInterventionService.resetData();
    }

    public async ngOnInit(): Promise<void> {
        this.fieldInterventionMapModeService.enterCreateSelectionMode();

        this.createFieldInterventionService.isSelectionEntitiesValidated.set(false);
        this.initFieldInterventionSelectionMode();

        const allLayer = await this.layerService.getAllLayers();
        const onlyTableLayer = allLayer.items.filter((layer) => layer.type === Layer.TypeEnum.TABLE);

        this.createFieldInterventionService.isLoading.set(true);

        if (!this.fieldInterventionBloc.value()) {
            this.createFieldInterventionService.isLoading.set(false);

            return;
        }

        this.createFieldInterventionService.showFieldInterventionAreaMask();

        await this.createFieldInterventionService.initFieldInterventionEntities(this.fieldInterventionBloc.value()!.id);
        this.layerService.setLayers(onlyTableLayer);
        await this.preselectLayerIfOnlyOneAvailable();
        this.createFieldInterventionService.isLoading.set(false);
    }

    private initFieldInterventionSelectionMode(): void {
        const isCreateSelectionPage = this.router.url.includes(RoutePathEnum.FIELD_INTERVENTION_CREATE);
        const isChangeSelectionPage = this.router.url.includes(RoutePathEnum.FIELD_INTERVENTION_CHANGE_SELECTION);

        this.createFieldInterventionService.isCreateFieldIntervention.set(isCreateSelectionPage);

        if (isChangeSelectionPage) {
            this.fieldInterventionChangeSelectionService.startFieldInterventionSelection();

            return;
        }

        this.fieldInterventionChangeSelectionService.stopFieldInterventionSelection();
    }

    private async preselectLayerIfOnlyOneAvailable(): Promise<void> {
        if (!this.layerListBloc.value()) {
            return;
        }

        const layers = this.layerListBloc
            .value()!
            .items.filter((layer: Layer) => layer.deposit && layer.type === Layer.TypeEnum.TABLE);

        if (layers.length === 1) {
            await this.createFieldInterventionService.changeActiveLayer(layers);

            if (!this.createFieldInterventionService.currentSources().length) {
                return;
            }
            await this.mapSourceService.drawSources(this.createFieldInterventionService.currentSources(), {
                fitBounds: true,
                intersectsFieldInterventionArea: this.createFieldInterventionService.fieldInterventionId(),
            });

            this.fieldInterventionSelectionModalService.openSelectionModal();
        }
    }
}
